import StepperModalForm from 'core/elements/modal/StepperModalForm'
import useParams from 'core/hooks/useParams'
import { Route } from 'core/plugins/route'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useState } from 'react'
import useReactRouter from 'use-react-router'
import { IAppsDetailsPageTabs } from '../../model'
import CreateTemplateBasicStep from './CreateTemplateBasicStep'
import CreateTemplateTerraformStep from './CreateTemplateTerraformStep'
import { getTemplateRequestBody } from '../helper'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { createTemplate, listRepositories, listTemplates } from '../../repositories/actions'
import { repositorySelector } from '../../repositories/selector'
import { useAppSelector } from 'app/store'

interface Props {
  addRoute: Route
  tabName: IAppsDetailsPageTabs
}

export default function CreateTemplate({ addRoute, tabName }: Props) {
  const { history } = useReactRouter()
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [missingVariableError, setMissingVariableError] = useState(false)
  const { reload } = useListAction(listTemplates, {})
  const { update, updating, error, reset } = useUpdateAction(createTemplate)

  const { message, loading, reload: reloadRepos } = useListAction(listRepositories)
  const repositoryDetails = useAppSelector(repositorySelector)

  const defaultParams = {
    name: '',
    templateId: '',
    description: '',
    terraformModulePath: '',
    creationStrategyType: 'managed',
    templateYaml: '',
    tag: '',
    variables: [],
    repoId: repositoryDetails?.[0]?.uid,
    icon: null,
    isModuleFetched: false,
    isFileDataMissing: false,
  }
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    history.push(
      routeManager.routes.openstack.appsDetails.details.path({
        tab: tabName,
      }),
    )
  }

  const submitForm = useCallback(async () => {
    if (params?.variables?.length) {
      const isValid = params?.variables?.every((item) => item?.key && item?.value)
      if (!isValid) {
        setMissingVariableError(true)
        return
      } else {
        setMissingVariableError(false)
      }
    }
    setAttemptedSubmit(true)
    const body = getTemplateRequestBody(params)
    const { success } = await update({ body })
    if (success) {
      handleClose()
      reload()
    }
    setAttemptedSubmit(false)
  }, [params, handleClose])

  const steps = [
    {
      stepId: 1,
      headerTitle: 'Basic Info',
      description: 'Define a reusable template to automate application deployments.',
      component: (
        <CreateTemplateBasicStep key="basic" params={params} getParamsUpdater={getParamsUpdater} />
      ),
    },
    {
      stepId: 2,
      headerTitle: 'Terraform',
      description: 'Specify a Terraform module to define application configuration.',
      component: (
        <CreateTemplateTerraformStep
          key="basic"
          params={params}
          getParamsUpdater={getParamsUpdater}
          missingVariableError={missingVariableError}
        />
      ),
    },
  ]
  useEffect(() => {
    reloadRepos(true)
  }, [])

  useEffect(() => {
    updateParams({
      repoId: repositoryDetails?.[0]?.uid,
    })
  }, [repositoryDetails])
  return (
    <StepperModalForm
      route={addRoute}
      onSubmit={submitForm}
      submitting={attemptedSubmit}
      submitTitle={'Create template'}
      disableSubmit={!params.isModuleFetched}
      isOnSubmitValidationRequired={false}
      error={error}
      resetError={reset}
      onClose={handleClose}
      title="Create New Template"
      steps={steps}
      maxWidth={900}
    />
  )
}
